/// <reference types="decagon" />
import { getBootstrapConfig } from '#pplx/frontend/apps/ask/bootstrap-config/bootstrapConfig.js';
import { useUserOrgData } from '#pplx/frontend/apps/ask/organization/hooks/useUserOrgData.js';
import { useGetDecagonUserAuth } from '#pplx/frontend/apps/ask/support/api/useGetDecagonUserAuth.js';
import { getMintlifyThreadIdFromParams, shouldOpenSupportWidgetFromParams, } from '#pplx/frontend/apps/ask/support/shouldOpenSupportWidgetFromParams.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { getCometBrowserInfo } from '#pplx/frontend/comet/client/utils.js';
import { loadScript } from '#pplx/frontend/common/loadScript.js';
import { sanitizedRedirectWindow } from '#pplx/frontend/common/sanitizedRedirectWindow.js';
import * as BUILD_CAPABILITIES from '#pplx/frontend/deployment-capabilities/build.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { isRecentBuild } from '#pplx/frontend/logger/isRecentBuild.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
// Auto-open is intentionally once per page session so multiple mounted entrypoints do not race.
let hasOpenedSupportWidgetFromUrlParam = false;
const DECAGON_APP_ID = 'l2wyozh0';
const SUPPORT_EMAIL_MAILTO = 'mailto:support@perplexity.ai';
const DECAGON_SCRIPT_ID = 'decagon-chat-loader';
const DECAGON_SCRIPT_SRC = 'https://decagon.ai/loaders/release/base/v1.js';
const DECAGON_TEAM_SLUG = 'N1HDpHo_perplexity';
const DECAGON_SCRIPT_TIMEOUT_MS = 10_000;
let decagonSdkSingleton;
let decagonScriptLoadAttempt = 0;
function compactMetadata(metadata) {
    const compacted = {};
    Object.entries(metadata).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            compacted[key] = value;
        }
    });
    return compacted;
}
function waitForDecagonReady(decagon) {
    if (decagon.isReady()) {
        return Promise.resolve(decagon);
    }
    return new Promise((resolve) => {
        decagon.setEventListener((event) => {
            if (event?.eventName === 'DECAGON_LOADED') {
                resolve(decagon);
            }
        });
    });
}
function removeDecagonScript(scriptId) {
    document.getElementById(scriptId)?.remove();
}
function getNextDecagonScriptId() {
    decagonScriptLoadAttempt += 1;
    return `${DECAGON_SCRIPT_ID}-${decagonScriptLoadAttempt}`;
}
function loadDecagonScript(scriptId) {
    return loadScript({
        id: scriptId,
        src: DECAGON_SCRIPT_SRC,
        async: true,
        attrs: {
            'data-slug': DECAGON_TEAM_SLUG,
        },
    }).catch((error) => {
        removeDecagonScript(scriptId);
        throw error;
    });
}
function withTimeout(promise, timeoutMs, errorMessage) {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
            reject(new Error(errorMessage));
        }, timeoutMs);
    });
    return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timeoutId !== undefined) {
            window.clearTimeout(timeoutId);
        }
    });
}
async function getReadyDecagonSdk(onScriptLoadStarted) {
    if (!window.duet) {
        const scriptId = getNextDecagonScriptId();
        onScriptLoadStarted(scriptId);
        await loadDecagonScript(scriptId);
    }
    if (!window.duet) {
        throw new Error('Decagon support widget loaded without initializing window.duet');
    }
    return waitForDecagonReady(window.duet);
}
function loadDecagonSdk() {
    if (decagonSdkSingleton) {
        return decagonSdkSingleton;
    }
    window.decagonConfig = {
        ...window.decagonConfig,
        defaultVisible: false,
        iframeTitle: 'Perplexity support chat',
    };
    let scriptId;
    decagonSdkSingleton = withTimeout(getReadyDecagonSdk((id) => {
        scriptId = id;
    }), DECAGON_SCRIPT_TIMEOUT_MS, 'Timed out while loading Decagon support widget').catch((error) => {
        decagonSdkSingleton = undefined;
        if (scriptId && !window.duet?.isReady()) {
            removeDecagonScript(scriptId);
        }
        throw error;
    });
    return decagonSdkSingleton;
}
export const useSupport = ({ data, reason, }) => {
    const { locale } = useIntl();
    const isCometBrowser = !!getCometBrowserInfo();
    const isLoggedIn = useIsUserLoggedIn();
    const { session } = useSession();
    const { isPro, isMax, subscriptionSource, subscriptionStatus, subscriptionTier } = useIsSubscribed();
    const { orgUser, isEnterprise } = useUserOrgData({ reason });
    const searchParams = useSearchParams();
    const { data: decagonUserAuth, refetch: fetchDecagonUserAuth } = useGetDecagonUserAuth();
    // Set only by the URL auto-open effect so ordinary support opens never carry a handoff ID.
    const pendingMintlifyThreadIdRef = useRef(undefined);
    const baseMetadata = useMemo(() => {
        const cometInfo = isCometBrowser ? { comet: true } : {};
        // Bootstrap config tells support which frontend bundle the user is on.
        // `minVersion` is the build timestamp (Unix ms); `is_recent_build` mirrors
        // the o11y derivation (see initializeClientO11y) so a stale/old-code client
        // is obvious in the support conversation without cross-referencing RUM.
        const bootstrapConfig = getBootstrapConfig();
        return {
            name: session?.user?.name, // Full name
            email: session?.user?.email, // Email address
            is_signed_in: isLoggedIn,
            is_pro_user: isPro,
            is_max_user: isMax,
            is_enterprise_user: isEnterprise,
            account_type: orgUser?.role,
            payment_tier: session?.user?.payment_tier || 'free',
            subscription_tier: subscriptionTier || 'none',
            app_version: bootstrapConfig.version || undefined,
            min_version: bootstrapConfig.minVersion || undefined,
            is_recent_build: isRecentBuild(bootstrapConfig.minVersion),
            ...data,
            ...cometInfo,
        };
    }, [
        session?.user?.email,
        session?.user?.name,
        session?.user?.payment_tier,
        data,
        isCometBrowser,
        isPro,
        isMax,
        isEnterprise,
        orgUser?.role,
        isLoggedIn,
        subscriptionTier,
    ]);
    // Decagon: build compacted metadata and lazily load the Decagon SDK on first show.
    const decagonMetadata = useMemo(() => {
        return compactMetadata({
            app_id: DECAGON_APP_ID,
            channel: 'chat',
            chat_origin: 'decagon_widget',
            locale,
            subscription_status: subscriptionStatus ?? 'none',
            subscription_source: subscriptionSource ?? 'none',
            ...baseMetadata,
        });
    }, [baseMetadata, locale, subscriptionSource, subscriptionStatus]);
    const showDecagon = useCallback(async () => {
        const userId = session?.user?.id;
        const mintlifyThreadId = pendingMintlifyThreadIdRef.current;
        pendingMintlifyThreadIdRef.current = undefined;
        // Prefer the prefetched auth; only fetch on demand if it hasn't resolved yet
        // (e.g. the user opens support before the enabled query settles).
        let auth = decagonUserAuth ?? null;
        if (userId && !auth) {
            const authResult = await fetchDecagonUserAuth();
            if (authResult.error) {
                throw authResult.error;
            }
            auth = authResult.data ?? null;
            if (!auth) {
                throw new Error('Missing Decagon user auth for signed-in user');
            }
        }
        const decagon = await loadDecagonSdk();
        if (auth) {
            const { userId: authUserId, signature, epoch } = auth;
            // Set the user id before setting the signed authentication information.
            decagon.setUserId(authUserId);
            decagon.setUserIdAuth(signature, epoch);
        }
        decagon.setMetadata(decagonMetadata);
        if (!userId || !mintlifyThreadId) {
            decagon.open();
            return;
        }
        const handoffModule = await import('#pplx/frontend/apps/ask/support/decagonMintlifyHandoff.js').catch(() => undefined);
        if (!handoffModule) {
            decagon.open();
            logger.warn('Mintlify thread ID not queued; opened support', {
                reason: 'module_load_failed',
            });
            return;
        }
        const handoffResult = await handoffModule.openDecagonWithMintlifyThreadId({
            decagon,
            threadId: mintlifyThreadId,
        });
        if (handoffResult !== 'queued') {
            logger.warn('Mintlify thread ID not queued; opened support', { reason: handoffResult });
        }
    }, [decagonMetadata, decagonUserAuth, fetchDecagonUserAuth, session?.user?.id]);
    const showSupportWidget = useCallback(async () => {
        if (!BUILD_CAPABILITIES.support) {
            sanitizedRedirectWindow(getBootstrapConfig().supportUrl ?? SUPPORT_EMAIL_MAILTO);
            return;
        }
        try {
            await showDecagon();
        }
        catch (error) {
            logger.error('Failed to open Decagon support widget; falling back to email', error);
            sanitizedRedirectWindow(SUPPORT_EMAIL_MAILTO);
        }
    }, [showDecagon]);
    useEffect(() => {
        if (!BUILD_CAPABILITIES.support)
            return;
        if (hasOpenedSupportWidgetFromUrlParam)
            return;
        if (!shouldOpenSupportWidgetFromParams(searchParams))
            return;
        hasOpenedSupportWidgetFromUrlParam = true;
        pendingMintlifyThreadIdRef.current = getMintlifyThreadIdFromParams(searchParams);
        void showSupportWidget();
    }, [searchParams, showSupportWidget]);
    return useMemo(() => ({ show: showSupportWidget }), [showSupportWidget]);
};
//# sourceMappingURL=useSupport.js.map